import type { OpenComposeParams } from "../mail/ComposeMailWindow";
import { antwortBetreff, baueAllenAntwortenEmpfaenger, baueAntwortZitat } from "../mail/antwortVorlage";
import type { ParsedMailFile } from "../../services/mailFileService";
import type { MailDateiAktionen } from "./MailFileViewer";

/**
 * T-247: Antworten, Allen antworten und Weiterleiten auf eine als Dokument
 * abgelegte Mail-Datei (`.eml`/`.msg`).
 *
 * Die Datei hat keine Postfach-Nachricht hinter sich — es gibt also kein
 * `replyToMessageId` und keine Verkettung beim Empfänger. Die Antwort ist eine
 * neue Mail mit „AW: …" und Zitat aus dem eigenen Postfach; der Vorgang, in
 * dem die Datei liegt, kommt als `vorgangId` mit, damit die gesendete Mail
 * dort erscheint.
 *
 * Weiterleiten hängt die Mail-Datei SELBST an (als KMVS-Dokument, der Server
 * holt die Bytes) — so reisen ihre Anhänge vollständig mit, ohne einen neuen
 * Anhang-Zeiger im Versand-Backend.
 */

/** Was der Aufrufer über das Dokument weiß. */
export type MailDateiKontext = {
    vorgangId?: number | null;
    contactId?: number | null;
    fileSize?: number | null;
};

const gemeinsamAus = (kontext?: MailDateiKontext): OpenComposeParams => {
    const vorgangId = kontext?.vorgangId ?? undefined;
    const contactId = kontext?.contactId ?? null;
    return {
        ...(vorgangId != null ? { vorgangId } : {}),
        contactId,
        empfaengerKontextId: contactId,
    };
};

const zitat = (mail: ParsedMailFile) =>
    baueAntwortZitat({
        datum: mail.date ? new Date(mail.date) : null,
        absenderName: mail.from?.name || mail.from?.address || "Unbekannt",
        absenderAdresse: mail.from?.name ? mail.from.address : null,
        html: mail.bodyHtml,
        kopfMaskieren: true,
    });

/**
 * T-219 (28.10.2026): nur Antworten und Allen antworten — für Mail-Dateien OHNE
 * Dokument-Id (Kundenakten-Ablage auf der Seite der alten Kundenakten).
 * Weiterleiten fehlt dort bewusst: es hängt die Datei als KMVS-Dokument an.
 */
export function baueMailDateiAntworten(p: {
    kontext?: MailDateiKontext;
    /**
     * Eigene Postfach-Adressen — „Allen antworten" lässt sie weg. Als Funktion
     * erst beim Klick gelesen: ein Fenster lebt länger als die Postfachliste,
     * die beim Öffnen vielleicht noch lud.
     */
    eigeneAdressen: string[] | (() => string[]);
    oeffne: (params: OpenComposeParams) => void;
}): Pick<MailDateiAktionen, "onAntworten" | "onAllenAntworten"> {
    const gemeinsam = () => gemeinsamAus(p.kontext);
    const eigene = () => (typeof p.eigeneAdressen === "function" ? p.eigeneAdressen() : p.eigeneAdressen);
    return {
        onAntworten: (mail) =>
            p.oeffne({
                ...gemeinsam(),
                to: mail.from?.address ? [mail.from.address] : [],
                subject: antwortBetreff("AW", mail.subject),
                body: zitat(mail),
                mode: "reply",
            }),
        onAllenAntworten: (mail) => {
            const { to, cc } = baueAllenAntwortenEmpfaenger(
                { von: mail.from?.address, an: mail.to, cc: mail.cc },
                eigene(),
            );
            p.oeffne({
                ...gemeinsam(),
                to,
                cc,
                subject: antwortBetreff("AW", mail.subject),
                body: zitat(mail),
                mode: "replyAll",
            });
        },
    };
}

export function baueMailDateiAktionen(p: {
    docId: number;
    fileName: string;
    mimeType?: string | null;
    kontext?: MailDateiKontext;
    /** Eigene Postfach-Adressen — „Allen antworten" lässt sie weg. */
    eigeneAdressen: string[];
    oeffne: (params: OpenComposeParams) => void;
}): MailDateiAktionen {
    const gemeinsam = () => gemeinsamAus(p.kontext);
    return {
        ...baueMailDateiAntworten({ kontext: p.kontext, eigeneAdressen: p.eigeneAdressen, oeffne: p.oeffne }),
        onWeiterleiten: (mail) =>
            p.oeffne({
                ...gemeinsam(),
                to: [],
                subject: antwortBetreff("WG", mail.subject),
                body: zitat(mail),
                mode: "forward",
                dokumentAnhaenge: [
                    {
                        documentId: p.docId,
                        name: p.fileName,
                        contentType: p.mimeType || "application/octet-stream",
                        size: p.kontext?.fileSize ?? 0,
                    },
                ],
            }),
    };
}
